// client/src/components/admin/AdminMoodCheckIns.jsx
import React, { useState, useEffect } from 'react';
import adminService from '../../services/adminService';
import '../../styles/components/admin/AdminMoodCheckIns.css';

const AdminMoodCheckIns = () => {
  const [analyticsData, setAnalyticsData] = useState(null);
  const [patientMoodHistory, setPatientMoodHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedDays, setSelectedDays] = useState(7);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState('date_desc');
  const [tenantFilter, setTenantFilter] = useState('All');
  const [tenants, setTenants] = useState([]);
  
  // New state variables for enhanced filtering
  const [patientFilter, setPatientFilter] = useState('');
  const [moodTypeFilter, setMoodTypeFilter] = useState('');
  const [averageFilter, setAverageFilter] = useState('');
  const [itemsPerPage, setItemsPerPage] = useState(10);

  const [selectedMoodEntry, setSelectedMoodEntry] = useState(null);
  const [showAnalysisModal, setShowAnalysisModal] = useState(false);
  const [rerunningAnalysis, setRerunningAnalysis] = useState({});
  const [analysisModalLoading, setAnalysisModalLoading] = useState(false);

  const [currentPage, setCurrentPage] = useState(1);

  // FINAL: Exact working mood URLs from Cloudinary (corrected)
  const moodSvgUrls = {
    great: 'https://res.cloudinary.com/dm7qxemrt/image/upload/v1749199687/Face_Im_Great_anfp2e.svg',
    good: 'https://res.cloudinary.com/dm7qxemrt/image/upload/v1749199687/Face_Im_good_ldcpo4.svg',
    okay: 'https://res.cloudinary.com/dm7qxemrt/image/upload/v1749199687/Face_Im_okay_tcf9cp.svg',
    struggling: 'https://res.cloudinary.com/dm7qxemrt/image/upload/v1749199687/Face_Im_struggling_regocn.svg',
    upset: 'https://res.cloudinary.com/dm7qxemrt/image/upload/v1749199687/Face_Im_upset_pqskxp.svg'
  };

  // Mood colors for consistency
  const moodColors = {
    great: '#4CAF50',
    good: '#8BC34A', 
    okay: '#FFC107',
    struggling: '#FF9800',
    upset: '#F44336'
  };

  // Get risk level badge with color
const getRiskLevelBadge = (riskLevel) => {
  const colors = {
    POSITIVE: '#4CAF50',
    LOW: '#8BC34A',
    MILD: '#FFC107',
    MODERATE: '#FF9800',
    HIGH: '#F44336',
    CRISIS: '#D32F2F',
    UNKNOWN: '#9E9E9E'
  };

  const color = colors[riskLevel] || colors.UNKNOWN;
  
  return (
    <span 
      className="risk-badge"
      style={{ 
        backgroundColor: color,
        color: 'white',
        padding: '4px 8px',
        borderRadius: '12px',
        fontSize: '11px',
        fontWeight: 'bold',
        display: 'inline-block'
      }}
    >
      {riskLevel}
    </span>
  );
};

// Handle opening analysis modal
const handleViewAnalysis = async (entry) => {
  console.log('📊 Opening NLP analysis for entry:', entry);
  setSelectedMoodEntry(entry);
  setShowAnalysisModal(true);
  
  // If the entry doesn't have sentiment data, fetch it
  if (!entry.sentiment && entry._id) {
    setAnalysisModalLoading(true);
    try {
      // Fetch full mood entry details if needed
      const response = await adminService.getIndividualPatientMoodAnalytics(
        entry.patientId, 
        7, 
        entry.tenantId
      );
      
      if (response.success && response.data.moodHistory) {
        const fullEntry = response.data.moodHistory.find(e => e._id === entry._id);
        if (fullEntry) {
          setSelectedMoodEntry(fullEntry);
        }
      }
    } catch (error) {
      console.error('Error fetching entry details:', error);
    } finally {
      setAnalysisModalLoading(false);
    }
  }
};

// Handle re-running analysis
const handleRerunAnalysis = async (entryId) => {
  try {
    console.log(`🔄 ADMIN: Re-running analysis for entry: ${entryId}`);
    
    setRerunningAnalysis(prev => ({ ...prev, [entryId]: true }));
    
    const result = await adminService.rerunSentimentAnalysisAdmin(entryId);
    
    console.log('✅ ADMIN: Reanalysis result:', result);
    
    if (result.success) {
      alert(result.data.changed.any 
        ? `Analysis updated: ${result.data.previousAnalysis?.type} → ${result.data.newAnalysis.type}`
        : 'Analysis confirmed (no significant changes)'
      );
      
      // Refresh the mood data
      await fetchPatientMoodHistory(selectedDays);
      
      // Update modal if it's open
      if (showAnalysisModal && selectedMoodEntry?._id === entryId) {
        handleViewAnalysis({ ...selectedMoodEntry, _id: entryId });
      }
    } else {
      throw new Error(result.message || 'Reanalysis failed');
    }
    
  } catch (error) {
    console.error('❌ ADMIN: Reanalysis error:', error);
    alert(error.message.includes('No reflection text') 
      ? 'Cannot reanalyze: This mood entry has no reflection text'
      : `Reanalysis failed: ${error.message}`
    );
  } finally {
    setRerunningAnalysis(prev => ({ ...prev, [entryId]: false }));
  }
};

// Get sentiment badge color
const getSentimentColor = (sentimentType) => {
  const colors = {
    positive: '#4CAF50',
    neutral: '#FFC107',
    negative: '#F44336'
  };
  return colors[sentimentType?.toLowerCase()] || '#9E9E9E';
};

  // Fetch tenants for filtering
  const fetchTenants = async () => {
    try {
      const response = await adminService.getTenants();
      if (response.success) {
        setTenants(response.data);
      }
    } catch (err) {
      console.error('Error fetching tenants:', err);
    }
  };

  // Fetch system-wide mood analytics
  const fetchAnalytics = async (days = 7) => {
    try {
      setLoading(true);
      setError(null);
      
      // TODO: Create this method in adminService.js
      const response = await adminService.getSystemWideMoodAnalytics(days, {
        tenantId: tenantFilter !== 'All' ? tenantFilter : undefined
      });

      if (response.success) {
        setAnalyticsData(response.data);
      } else {
        setError('Failed to fetch analytics data');
      }
    } catch (err) {
      console.error('Error fetching system mood analytics:', err);
      setError(err.response?.data?.message || 'Failed to fetch mood analytics');
    } finally {
      setLoading(false);
    }
  };

  // Fetch system-wide patient mood history
  const fetchPatientMoodHistory = async (days) => {
  try {
    setLoading(true);
    setError(null);
    
    console.log('🔍 Fetching system mood history for', days, 'days');
    
    // ✅ FIX: Use correct method signature with filters object
    const response = await adminService.getSystemWideMoodHistory(days, {
      search: searchTerm,
      sortBy: sortBy,
      tenantId: null // or your selected tenant filter
    });
    
    console.log('📊 Raw API Response:', response);
    
    if (response.success && response.data) {
      console.log('✅ Processing mood data:', response.data.length, 'entries');
      
      // Transform the data to match expected structure
      const processedData = response.data.map(entry => {
        console.log('Processing entry:', entry);
        
        return {
          _id: entry._id,
          patientId: entry.userId?._id || entry.userId,
          patientName: entry.patientName || 'Unknown',
          date: entry.timestamp || entry.createdAt || new Date(),
          averageRating: entry.moodRating || 0,
          totalLogs: 1,
          lastMood: {
            moodKey: entry.moodKey || 'okay',
            moodLabel: entry.moodLabel || entry.moodKey || 'Unknown',
            moodRating: entry.moodRating || 0,
            timestamp: entry.timestamp || entry.createdAt
          },
          sentiment: entry.sentiment || null,
          reflection: entry.reflection || '',
          tenantId: entry.tenantId,
          tenantName: entry.tenantName
        };
      });
      
      console.log('✅ Processed data:', processedData);
      setPatientMoodHistory(processedData);
      
    } else {
      throw new Error(response.message || 'Failed to fetch mood history');
    }
  } catch (err) {
    console.error('❌ Error fetching mood history:', err);
    setError(err.message || 'Failed to load mood check-in data');
    setPatientMoodHistory([]);
  } finally {
    setLoading(false);
  }
};

  // Load data on component mount and when filters change
  useEffect(() => {
    fetchTenants();
  }, []);

  useEffect(() => {
  setCurrentPage(1);
}, [searchTerm, patientFilter, moodTypeFilter, averageFilter, itemsPerPage]);

  useEffect(() => {
    fetchAnalytics(selectedDays);
    fetchPatientMoodHistory(selectedDays);
  }, [selectedDays, tenantFilter]);

  useEffect(() => {
    // Debounce search and fetch history
    const timeoutId = setTimeout(() => {
      fetchPatientMoodHistory(selectedDays);
    }, 300);
    
    return () => clearTimeout(timeoutId);
  }, [searchTerm, sortBy, patientFilter, moodTypeFilter, averageFilter]);

  // Handle days filter change
  const handleDaysChange = (days) => {
    setSelectedDays(days);
  };

  // Handle tenant filter change
  const handleTenantChange = (tenantId) => {
    setTenantFilter(tenantId);
  };

  // Format mood key for display
  const formatMoodLabel = (moodKey) => {
    return moodKey.charAt(0).toUpperCase() + moodKey.slice(1);
  };

  // Get mood SVG URL
  const getMoodSvg = (moodKey) => {
    return moodSvgUrls[moodKey] || moodSvgUrls['okay'];
  };

  // NLP Analysis Modal Component
const NLPAnalysisModal = () => {
  if (!showAnalysisModal || !selectedMoodEntry) return null;

  const sentiment = selectedMoodEntry.sentiment;
  const hasReflection = selectedMoodEntry.reflection && selectedMoodEntry.reflection.trim().length > 0;

  return (
    <div className="nlp-modal-overlay" onClick={() => setShowAnalysisModal(false)}>
      <div className="nlp-modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="nlp-modal-header">
          <h2>NLP Mood Analysis</h2>
          <button 
            className="nlp-modal-close" 
            onClick={() => setShowAnalysisModal(false)}
          >
            ×
          </button>
        </div>

        <div className="nlp-modal-body">
          {/* Entry Info */}
          <div className="nlp-section">
            <h3>Mood Entry Details</h3>
            {/* First Section: Patient and Date in 2 columns */}
              <div className="nlp-entry-info-grid">
                <div className="nlp-info-item">
                  <span className="nlp-label">Patient:</span>
                  <span className="nlp-value">{selectedMoodEntry.patientName || 'Unknown'}</span>
                </div>
                <div className="nlp-info-item">
                  <span className="nlp-label">Date:</span>
                  <span className="nlp-value">
                    {new Date(selectedMoodEntry.date).toLocaleString()}
                  </span>
                </div>
              </div>

              {/* Second Section: Mood taking full width */}
              <div className="nlp-mood-info-full">
                <div className="nlp-info-item">
                  <span className="nlp-label">Mood:</span>
                  <span 
                      className={`nlp-value nlp-mood-${selectedMoodEntry.lastMood?.moodKey || 'default'}`}
                    >
                      <img 
                        src={getMoodSvg(selectedMoodEntry.lastMood?.moodKey)} 
                        alt={selectedMoodEntry.lastMood?.moodKey}
                        style={{ width: '40px', height: '40px', marginRight: '8px', verticalAlign: 'middle' }}
                      />
                      <span style={{
                        color: selectedMoodEntry.lastMood?.moodKey === 'great' ? '#6FBF46' :
                              selectedMoodEntry.lastMood?.moodKey === 'good' ? '#AFD15D' :
                              selectedMoodEntry.lastMood?.moodKey === 'okay' ? '#EFD700' :
                              selectedMoodEntry.lastMood?.moodKey === 'struggling' ? '#FFA05C' :
                              selectedMoodEntry.lastMood?.moodKey === 'upset' ? '#FF7676' : '#333'
                      }}>
                        {selectedMoodEntry.lastMood?.moodLabel}
                      </span>
                    </span>
                </div>
              </div>
          </div>

          {/* Reflection Text */}
          {hasReflection && (
            <div className="nlp-section">
              <span className="nlp-label">Patient Reflection:</span>
              <div className="nlp-reflection-box">
                {selectedMoodEntry.reflection}
              </div>
            </div>
          )}

          <div className="system-mood-header-divider"></div>

          {/* Sentiment Analysis */}
          {sentiment ? (
            <>
              <div className="nlp-section">
                <h3>AI Sentiment Analysis</h3>
                <div className="nlp-sentiment-grid">
                  <div className="nlp-sentiment-item">
                    <span className="nlp-label">Sentiment Type:</span>
                    <span 
                      className="nlp-sentiment-badge"
                      style={{ backgroundColor: getSentimentColor(sentiment.type) }}
                    >
                      {sentiment.type}
                    </span>
                  </div>
                  
                  <div className="nlp-sentiment-item">
                    <span className="nlp-label">Confidence Score:</span>
                    <span className="nlp-confidence-badge">{sentiment.score}%</span>
                  </div>
                  
                  <div className="nlp-sentiment-item">
                    <span className="nlp-label">Risk Level:</span>
                    {getRiskLevelBadge(sentiment.riskLevel)}
                  </div>
                  
                  {sentiment.moodValidation && (
                    <div className="nlp-sentiment-item">
                      <span className="nlp-label">Mood Validation:</span>
                      <span className="nlp-mood-validation">
                        {sentiment.moodValidation.status}
                      </span>
                    </div>
                  )}
                </div>
              </div>

              <div className="system-mood-header-divider"></div>

              {/* Clinical Notes */}
              {sentiment.clinicalNotes && (
                <div className="nlp-section">
                  <h3>Clinical Notes</h3>
                  <div className="nlp-clinical-notes">
                    {sentiment.clinicalNotes}
                  </div>
                </div>
              )}

              <div className="system-mood-header-divider"></div>

              {/* Re-run Analysis Button */}
              <div className="nlp-section">
                <button
                  className="nlp-rerun-button"
                  onClick={() => handleRerunAnalysis(selectedMoodEntry._id)}
                  disabled={rerunningAnalysis[selectedMoodEntry._id] || !hasReflection}
                >
                  {rerunningAnalysis[selectedMoodEntry._id] ? (
                    <>
                      <span className="spinner">⟳</span>
                      Re-running...
                    </>
                  ) : (
                    <>
                      Re-run AI Analysis
                    </>
                  )}
                </button>
                {!hasReflection && (
                  <p className="nlp-warning">
                    Cannot re-run analysis: No reflection text available
                  </p>
                )}
              </div>
            </>
          ) : (
            <div className="nlp-section">
              <div className="nlp-no-analysis">
                <p>No sentiment analysis available for this entry.</p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

  // Get mood color
  const getMoodColor = (moodKey) => {
    return moodColors[moodKey] || '#FFC107';
  };

  // Format day name (Mon, Tue, Wed, etc.)
  const formatDayName = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { weekday: 'long' });
  };

  // Get tenant name by ID
  const getTenantName = (tenantId) => {
    const tenant = tenants.find(t => t._id === tenantId);
    return tenant ? tenant.name : 'Unknown Clinic';
  };

  // Enhanced filter and sort patient mood history
  const getFilteredMoodHistory = () => {
  let filtered = patientMoodHistory.filter(entry => {
    // Search term filter
    if (searchTerm && !entry.patientName?.toLowerCase().includes(searchTerm.toLowerCase())) {
      return false;
    }
    
    // Patient filter
    if (patientFilter && entry.patientName !== patientFilter) {
      return false;
    }
    
    // Mood type filter
    if (moodTypeFilter && entry.lastMood?.moodKey !== moodTypeFilter) {
      return false;
    }
    
    // Average rating filter
    if (averageFilter) {
      const avgRating = parseFloat(entry.averageRating) || 0;
      switch (averageFilter) {
        case 'high':
          if (avgRating < 4.0) return false;
          break;
        case 'medium':
          if (avgRating < 2.0 || avgRating >= 4.0) return false;
          break;
        case 'low':
          if (avgRating >= 2.0) return false;
          break;
        default:
          break;
      }
    }
    
    return true;
  });

  // Sort data
  filtered.sort((a, b) => {
    switch (sortBy) {
      case 'date_asc':
        return new Date(a.date) - new Date(b.date);
      case 'date_desc':
        return new Date(b.date) - new Date(a.date);
      case 'mood':
        return (a.lastMood?.moodRating || 0) - (b.lastMood?.moodRating || 0);
      case 'average':
        return (parseFloat(a.averageRating) || 0) - (parseFloat(b.averageRating) || 0);
      case 'tenant':
        return getTenantName(a.tenantId).localeCompare(getTenantName(b.tenantId));
      case 'patient':
        return (a.patientName || '').localeCompare(b.patientName || '');
      default:
        return new Date(b.date) - new Date(a.date);
    }
  });

  // Paginate
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  
  return {
    data: filtered.slice(startIndex, endIndex),
    totalCount: filtered.length,
    totalPages: Math.ceil(filtered.length / itemsPerPage)
  };
};

  // Handle export functionality
  const handleExportPDF = async () => {
    try {
      // TODO: Create this method in adminService.js
      const response = await adminService.exportSystemMoodAnalytics({
        days: selectedDays,
        tenantId: tenantFilter !== 'All' ? tenantFilter : undefined,
        search: searchTerm,
        patientFilter: patientFilter,
        moodTypeFilter: moodTypeFilter,
        averageFilter: averageFilter
      });
      
      if (response.success) {
        // File should be downloaded automatically
        console.log('Export successful');
      }
    } catch (err) {
      console.error('Error exporting mood data:', err);
      setError('Failed to export data');
    }
  };

  if (loading) {
    return (
      <div className="system-mood-analytics-container">
        <div className="system-mood-loading-container">
          <div className="system-mood-loading-spinner"></div>
          <p>Loading system mood analytics...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="system-mood-analytics-container">
        <div className="system-mood-error-container">
          <h3>Error Loading Data</h3>
          <p>{error}</p>
          <button onClick={() => fetchAnalytics(selectedDays)} className="system-mood-retry-button">
            Try Again
          </button>
        </div>
      </div>
    );
  }

  if (!analyticsData) {
    return (
      <div className="system-mood-analytics-container">
        <div className="system-mood-no-data-container">
          <h3>No Data Available</h3>
          <p>No mood check-in data found for the selected period.</p>
        </div>
      </div>
    );
  }

  const keyMetrics = analyticsData.keyMetrics || {
    totalLogs: 0,
    averageLogsPerDay: 0,
    averageMoodScore: 0,
    topEmotionalTrends: []
  };

  const dailyOverview = analyticsData.dailyOverview || [];
  const moodDistribution = analyticsData.moodDistribution || {};

  return (
    <div className="system-mood-analytics-container">
      {/* Admin Header - using admin styling patterns */}
      <div className="system-mood-analytics-header">
        <div className="system-mood-header-content">
          <div className="system-mood-header-left">
            <h1>Patient Mood Check-Ins</h1>
            <p>
              Honest reflections and mood updates from those working through life’s challenges — one feeling at a time.
            </p>
          </div>
        </div>
        <div className="system-mood-header-divider"></div>
      </div>

      {/* Filter Section - following admin pattern */}
      <div className="system-mood-actions-bar">
        <div className="system-mood-time-filter">
          <label>Time Period:</label>
          <select 
            value={selectedDays} 
            onChange={(e) => handleDaysChange(parseInt(e.target.value))}
            className="system-mood-days-select"
          >
            <option value={7}>Last 7 days</option>
            <option value={14}>Last 14 days</option>
            <option value={30}>Last 30 days</option>
          </select>
        </div>
        
        <div className="system-mood-filter-item">
          <label htmlFor="tenant-filter">Clinic:</label>
          <select 
            id="tenant-filter" 
            value={tenantFilter} 
            onChange={(e) => handleTenantChange(e.target.value)}
          >
            <option value="All">All Clinics</option>
            {tenants.map(tenant => (
              <option key={tenant._id} value={tenant._id}>
                {tenant.name}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Key Metrics - using admin card style */}
      <div className="system-mood-metrics-section">
        <h2>System Overview</h2>
        <div className="system-mood-metrics-grid">
          <div className="system-mood-metric-card">
            <div className="stat-label">Total Logs</div>
            <div className="stat-value-container">
              <div className="admin-wide-sys-stat-value">{keyMetrics.totalLogs}</div>
            </div>
          </div>
          <div className="system-mood-metric-card">
            <div className="stat-label">Avg. Logs/Day</div>
            <div className="stat-value-container">
              <div className="admin-wide-sys-stat-value">{keyMetrics.averageLogsPerDay}</div>
            </div>
          </div>
          <div className="system-mood-metric-card">
            <div className="stat-label">Avg. Mood Score</div>
            <div className="stat-value-container">
              <div className="admin-wide-sys-stat-value">{keyMetrics.averageMoodScore}</div>
            </div>
          </div>
         <div className="system-mood-metric-card top-trend-card">
            <div className="stat-label">Top Trend</div>
            <div className="stat-value-container">
              {keyMetrics.topEmotionalTrends.length > 0 ? (
                <>
                  <div className="trend-emoji-container">
                    <img 
                      src={getMoodSvg(keyMetrics.topEmotionalTrends[0].mood.toLowerCase())} 
                      alt={keyMetrics.topEmotionalTrends[0].mood}
                      className="trend-svg"
                    />
                  </div>
                  <div className="trend-text-container">
                    <span>{keyMetrics.topEmotionalTrends[0].mood}</span>
                  </div>
                </>
              ) : (
                <span>No data</span>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="system-mood-header-divider"></div>

      {/* Daily Overview */}
      <div className="system-mood-daily-overview-section">
  <h2>Daily Overview</h2>
  <div className="system-mood-daily-grid">
    {dailyOverview.map((day, index) => (
      <div key={index} className="system-mood-daily-item">
        <div className="system-mood-daily-month">
          {new Date(day.date).toLocaleDateString('en-US', { month: 'short' })}
        </div>
        <div className="system-mood-daily-date">{day.dateFormatted}</div>
        <div className="system-mood-daily-day-name">{formatDayName(day.date)}</div>
        <div className="system-mood-daily-svg-wrapper">
          <img 
            src={getMoodSvg(day.mostFrequentMood)} 
            alt={day.mostFrequentMood}
            className="system-mood-daily-svg"
          />
        </div>
        <div className="system-mood-daily-stats">
          <div className="system-mood-daily-score">{day.averageMoodScore}</div>
          <div className="system-mood-daily-entries">{day.totalEntries} logs</div>
        </div>
      </div>
    ))}
  </div>
</div>
      
      {/* Mood Distribution */}
      <div className="system-mood-distribution-section">
        <h2>Mood Distribution</h2>
        <div className="system-mood-distribution-container">
          <div className="system-mood-distribution-grid">
            {Object.entries(moodDistribution).map(([moodKey, data]) => (
              <div key={moodKey} className={`system-mood-distribution-item ${moodKey}`}>
                <div className="system-mood-distribution-info">
                  <img 
                    src={getMoodSvg(moodKey)} 
                    alt={moodKey}
                    className="mood-svg-icon"
                  />
                  <span className="admin-system-wide-mood-label">I'm {moodKey}</span>
                </div>
                <div className="system-mood-distribution-stats">
                  <div className="admin-system-wide-mood-count">{data.count} logs</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="system-mood-header-divider"></div>

      {/* Patient Mood History Table */}
      <div className="system-mood-history-section">
        <h2>Mood Check-In History</h2>
        
        {/* Enhanced Controls Section */}
        <div className="system-mood-history-controls">
  {/* First Row: Search, Patient Filter, and Show entries */}
  <div className="system-mood-search-row">
    <div className="system-mood-search-container">
      <div className="system-mood-search-input-wrapper">
        <div className="system-mood-search-icon"></div>
        <input 
          type="text" 
          placeholder="Search" 
          className="system-mood-search-input"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
      </div>
    </div>
    
    <div className="system-mood-filter-item-inline">
      <label>Patient</label>
      <select 
        className="system-mood-filter-select"
        value={patientFilter || 'All'}
        onChange={(e) => setPatientFilter(e.target.value === 'All' ? '' : e.target.value)}
      >
        <option value="All">All</option>
        {[...new Set(patientMoodHistory.map(entry => entry.patientName))].map(name => (
          <option key={name} value={name}>{name}</option>
        ))}
      </select>
    </div>
    
    <div className="system-mood-export-section">
        <label>Show</label>
        <select 
          className="system-mood-entries-select"
          value={itemsPerPage}
          onChange={(e) => setItemsPerPage(parseInt(e.target.value))}
        >
          <option value={10}>10</option>
          <option value={25}>25</option>
          <option value={50}>50</option>
        </select>
      </div>
  </div>
  
  {/* Second Row: End-of-Day Mood Filter, Average Filter, and Export PDF */}
  <div className="system-mood-filter-row">
    <div className="system-mood-filter-item-inline">
      <label>End-of-Day Mood Check-In Log</label>
      <select 
        className="system-mood-filter-select"
        value={moodTypeFilter || 'All'}
        onChange={(e) => setMoodTypeFilter(e.target.value === 'All' ? '' : e.target.value)}
      >
        <option value="All">All</option>
        <option value="great">I'm great</option>
        <option value="good">I'm good</option>
        <option value="okay">I'm okay</option>
        <option value="struggling">I'm struggling</option>
        <option value="upset">I'm upset</option>
      </select>
    </div>
    
    <div className="system-mood-filter-item-inline">
      <label>Average</label>
      <select 
        className="system-mood-filter-select"
        value={averageFilter || 'All'}
        onChange={(e) => setAverageFilter(e.target.value === 'All' ? '' : e.target.value)}
      >
        <option value="All">All</option>
        <option value="high">4.0 - 5.0</option>
        <option value="medium">2.0 - 3.9</option>
        <option value="low">1.0 - 1.9</option>
      </select>
    </div>
    
    <div className="system-mood-export-section">
      <button className="system-mood-export-btn" onClick={handleExportPDF}>
        Export PDF
      </button>
    </div>
  </div>
</div>

        {/* Table Section */}
        <div style={{ marginBottom: '30px', overflowX: 'auto' }}>
          <table style={{ 
            width: '100%', 
            borderCollapse: 'separate', 
            borderSpacing: '0 10px' 
          }}>
            <thead>
              <tr style={{ backgroundColor: 'rgba(167, 215, 197, 0.1)' }}>
                <th style={{
                  padding: '12px 16px',
                  textAlign: 'left',
                  borderTopLeftRadius: '12px',
                  borderBottomLeftRadius: '12px',
                  fontSize: '15px',
                  fontFamily: 'IBM Plex Mono, monospace',
                  color: '#274137',
                  fontWeight: 700
                }}>Date</th>
                <th style={{
                  padding: '12px 16px',
                  textAlign: 'left',
                  fontSize: '15px',
                  fontFamily: 'IBM Plex Mono, monospace',
                  color: '#274137',
                  fontWeight: 700
                }}>Patient</th>
                <th style={{
                  padding: '12px 16px',
                  textAlign: 'left',
                  fontSize: '15px',
                  fontFamily: 'IBM Plex Mono, monospace',
                  color: '#274137',
                  fontWeight: 700
                }}>Average</th>
                <th style={{
                  padding: '12px 16px',
                  textAlign: 'left',
                  fontSize: '15px',
                  fontFamily: 'IBM Plex Mono, monospace',
                  color: '#274137',
                  fontWeight: 700
                }}>Total Logs</th>
                <th style={{
                  padding: '12px 16px',
                  textAlign: 'left',
                  fontSize: '15px',
                  fontFamily: 'IBM Plex Mono, monospace',
                  color: '#274137',
                  fontWeight: 700
                }}>End-of-Day Mood Check-In Log</th>
                <th style={{
                  padding: '12px 16px',
                  textAlign: 'left',
                  borderTopRightRadius: '12px',
                  borderBottomRightRadius: '12px',
                  fontSize: '15px',
                  fontFamily: 'IBM Plex Mono, monospace',
                  color: '#274137',
                  fontWeight: 700
                }}>Actions</th>
              </tr>
            </thead>
            <tbody>
            {(() => {
              const { data, totalCount, totalPages } = getFilteredMoodHistory();
              return data.length > 0 ? (
                data.map((entry, index) => {
                  const isEven = index % 2 === 1;
                  
                  return (
                    <tr key={`${entry.patientId}-${entry.date}-${index}`} style={isEven ? { backgroundColor: 'rgba(167, 215, 197, 0.1)' } : { backgroundColor: '#ffffff' }}>
                      <td style={{
                        padding: '12px 16px',
                        borderTopLeftRadius: '12px',
                        borderBottomLeftRadius: '12px',
                        fontSize: '15px',
                        fontFamily: 'IBM Plex Mono, monospace',
                        verticalAlign: 'middle'
                      }}>
                        {new Date(entry.date).toLocaleDateString('en-US', { 
                          year: 'numeric', 
                          month: 'long', 
                          day: 'numeric' 
                        })}
                      </td>
                      
                      <td style={{
                        padding: '12px 16px',
                        fontSize: '15px',
                        fontFamily: 'IBM Plex Mono, monospace',
                        verticalAlign: 'middle'
                      }}>
                        {entry.patientName || 'Unknown Patient'}
                      </td>
                      
                      <td style={{
                        padding: '12px 16px',
                        fontSize: '15px',
                        fontFamily: 'IBM Plex Mono, monospace',
                        verticalAlign: 'middle'
                      }}>
                        {entry.averageRating ? `${parseFloat(entry.averageRating).toFixed(1)}/5` : 'N/A'}
                      </td>
                      
                      <td style={{
                        padding: '12px 16px',
                        fontSize: '15px',
                        fontFamily: 'IBM Plex Mono, monospace',
                        verticalAlign: 'middle'
                      }}>
                        {entry.totalLogs} logs
                      </td>
                      
                      <td style={{
                        padding: '12px 16px',
                        fontSize: '15px',
                        fontFamily: 'IBM Plex Mono, monospace',
                        verticalAlign: 'middle'
                      }}>
                        {entry.lastMood ? (
                          <span style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '8px',
                            padding: '6px 12px',
                            borderRadius: '8px',
                            fontSize: '14px',
                            fontWeight: 500,
                            fontFamily: 'IBM Plex Mono, monospace',
                            backgroundColor: entry.lastMood.moodKey === 'great' ? 'rgba(58, 131, 20, 0.1)' :
                                           entry.lastMood.moodKey === 'good' ? 'rgba(122, 146, 65, 0.1)' :
                                           entry.lastMood.moodKey === 'okay' ? 'rgba(154, 140, 6, 0.1)' :
                                           entry.lastMood.moodKey === 'struggling' ? 'rgba(162, 97, 51, 0.1)' :
                                           entry.lastMood.moodKey === 'upset' ? 'rgba(148, 71, 71, 0.1)' : 'rgba(154, 140, 6, 0.1)',
                            color: entry.lastMood.moodKey === 'great' ? '#3A8314' :
                                   entry.lastMood.moodKey === 'good' ? '#7A9241' :
                                   entry.lastMood.moodKey === 'okay' ? '#9A8C06' :
                                   entry.lastMood.moodKey === 'struggling' ? '#A26133' :
                                   entry.lastMood.moodKey === 'upset' ? '#944747' : '#9A8C06',
                            border: entry.lastMood.moodKey === 'great' ? '1px solid rgba(58, 131, 20, 0.2)' :
                                    entry.lastMood.moodKey === 'good' ? '1px solid rgba(122, 146, 65, 0.2)' :
                                    entry.lastMood.moodKey === 'okay' ? '1px solid rgba(154, 140, 6, 0.2)' :
                                    entry.lastMood.moodKey === 'struggling' ? '1px solid rgba(162, 97, 51, 0.2)' :
                                    entry.lastMood.moodKey === 'upset' ? '1px solid rgba(148, 71, 71, 0.2)' : '1px solid rgba(154, 140, 6, 0.2)'
                          }}>
                            <img 
                              src={getMoodSvg(entry.lastMood.moodKey)} 
                              alt={entry.lastMood.moodKey}
                              style={{ width: '16px', height: '16px', marginRight: '4px', verticalAlign: 'middle' }}
                            />
                            {entry.lastMood.moodLabel || `I'm ${entry.lastMood.moodKey}`}
                          </span>
                        ) : (
                          <span style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '8px',
                            padding: '6px 12px',
                            borderRadius: '8px',
                            fontSize: '14px',
                            fontWeight: 500,
                            fontFamily: 'IBM Plex Mono, monospace',
                            backgroundColor: 'rgba(154, 140, 6, 0.1)',
                            color: '#9A8C06',
                            border: '1px solid rgba(154, 140, 6, 0.2)'
                          }}>
                            <img 
                              src={getMoodSvg('okay')} 
                              alt="okay"
                              style={{ width: '16px', height: '16px', marginRight: '4px', verticalAlign: 'middle' }}
                            />
                            No recent mood
                          </span>
                        )}
                      </td>
                      
                      <td style={{
                          padding: '12px 16px',
                          borderTopRightRadius: '12px',
                          borderBottomRightRadius: '12px'
                        }}>
                        <button
  style={{
    width: '70%',
    padding: '8px 16px',
    borderRadius: '10px',
    backgroundColor: entry.sentiment ? '#274137' : '#274137',
    color: '#FFFFFF', // White text color
    fontFamily: 'IBM Plex Mono', // IBM Plex Mono font
    fontWeight: 'bold', // Bold font weight
    border: 'none', // Remove default border
    cursor: 'pointer',
    fontSize: '14px'
  }}
  onClick={() => handleViewAnalysis(entry)}
>
  {entry.sentiment ? 'View Analysis' : '👁️ View Details'}
</button>
                      </td>
                    </tr>
                  );
                 })
              ) : (
                <tr>
                  <td colSpan={6} style={{ 
                    backgroundColor: '#fff', 
                    border: '1px solid #A7D7C5', 
                    borderRadius: '12px', 
                    padding: '40px', 
                    textAlign: 'center', 
                    fontSize: '16px',
                    fontFamily: 'IBM Plex Mono, monospace',
                    color: '#666'
                  }}>
                    {totalCount === 0
                      ? 'No mood check-in data available in the system'
                      : 'No patients match your search criteria'
                    }
                  </td>
                </tr>
              );
            })()}
            </tbody>
          </table>
        </div>
      </div>
      {/* NLP Analysis Modal */}
      <NLPAnalysisModal />
    </div>
  );
};

export default AdminMoodCheckIns;